import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation, useOutletContext } from "react-router-dom";
import axios from "axios";
import AppointmentCalendar from "../Calendar";
import CONFIG from '../../config';
import { useAuth } from "../../context/AuthProvider";
import { usePatient } from "../../context/PatientContext";
import { useTheme } from "../../context/ThemeContext";

const EditPatientNextVisit = () => {
  const { markSectionComplete } = useOutletContext();
  const [token] = useState(localStorage.getItem("authToken"));
  const navigate = useNavigate();
  const { verifyTokenAndRedirect } = useAuth();
  const {
    patientData,
    setPatientData,
    updateExistingCaseVisit,
    addNewCaseVisit,
    addExistingCaseVisit,
    selectedCase,
    selectedVisit,
    doctorInfo
  } = usePatient();
  const { theme } = useTheme();

  const { patientId, caseId, visitId } = useParams();
  const location = useLocation();
  const [allAppointments, setAllAppointments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    verifyTokenAndRedirect();
  }, []);

  // ✅ FIXED: Wrap in useCallback and accept date parameter
  const fetchAllAppointments = useCallback(async (date = new Date()) => {
    if (!doctorInfo?.MANDALI_ID || !patientData?.PATIENT_ID) {
      console.log('Doctor info or patient data not available yet');
      return;
    }

    try {
      setLoading(true);

      // Format the date as YYYY-MM-DD
      const dateStr = date instanceof Date
        ? date.toISOString().split('T')[0]
        : new Date(date).toISOString().split('T')[0];

      console.log('Fetching appointments for date:', dateStr);

      const response = await axios.get(`${CONFIG.BASE_URL}/doctors/calendar/appointments`, {
        params: {
          view: 'month',
          date: dateStr,
          patientId: patientData.PATIENT_ID
        },
        headers: {
          Authorization: `Bearer ${token}`,
          doctorId: doctorInfo.MANDALI_ID,
          clinicId: doctorInfo.CLINIC_ID,
          roleId: doctorInfo.MANDALI_ROLE,
          "Content-Type": "application/json"
        }
      });

      if (response.data.success) {
        setAllAppointments(response.data.appointments);
        console.log('Appointments fetched successfully:', response.data.appointments.length);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  }, [doctorInfo, patientData?.PATIENT_ID, token]); // Dependencies

  // Initial fetch when dependencies are ready
  useEffect(() => {
    if (doctorInfo?.MANDALI_ID && patientData?.PATIENT_ID) {
      fetchAllAppointments();
    }
  }, [doctorInfo?.MANDALI_ID, patientData?.PATIENT_ID, fetchAllAppointments]);

  const determineVisitAction = () => {
    if (!patientData) return null;
    const actionStatus = patientData?.ACTION_STATUS;

    if (actionStatus === "UPDATE_EXISTING_VISIT") {
      return "updateExistingCaseVisit";
    } else if (actionStatus === "ADD_NEW_CASE") {
      return "addNewCaseVisit";
    } else if (actionStatus === "ADD_NEW_VISIT") {
      return "addExistingCaseVisit";
    }

    return null;
  };

  const onSubmitHandler = async (updatedPatientNextVisit, options = {}) => {
    const isAutoSave = options?.isAutoSave;
    console.log("Updated next visit data:", updatedPatientNextVisit);
    const { duration, ...cleanPayload } = updatedPatientNextVisit;
    const updatedData = {
      ...patientData,
      patientNextVisit: patientData?.patientNextVisit?.length > 0
        ? patientData.patientNextVisit.map(info => ({
          ...info,
          ...cleanPayload,
          PATIENT_ID: patientData.PATIENT_ID,
          CASE_ID: selectedCase || caseId,
          VISIT_ID: selectedVisit || visitId,
          DOCTOR_ID: doctorInfo.MANDALI_ID,
          CLINIC_ID: doctorInfo.CLINIC_ID
        }))
        : [{
          ...cleanPayload,
          PATIENT_ID: patientData.PATIENT_ID,
          CASE_ID: selectedCase || caseId,
          VISIT_ID: selectedVisit || visitId,
          DOCTOR_ID: doctorInfo.MANDALI_ID,
          CLINIC_ID: doctorInfo.CLINIC_ID
        }]
    };

    setPatientData(updatedData);

    console.log('Overall patient data before backend call:', updatedData);

    const updateType = determineVisitAction();

    if (updateType) {
      console.log(`Executing ${updateType}...`);
      await handleVisitUpdate(updateType, updatedData);

      // ✅ Refresh appointments from backend after successful save
      await fetchAllAppointments();
    }

    console.log("Updated patient data after Next Visit update:", updatedData);
    markSectionComplete("nextvisit");

    if (!isAutoSave) {
      if (location.pathname.startsWith("/addpatient")) {
        setPatientData(null);
        navigate("/patientlist");
      } else if (location.pathname.startsWith("/visits")) {
        setPatientData(null);
        navigate(`/visits/${patientId}/${caseId}`);
      } else {
        console.warn("Unknown route, no redirection");
      }
    }
  };

  const handleVisitUpdate = async (updateType, visitData) => {
    try {
      if (!visitData) return;
      console.log("Handling visit update:", updateType);

      const payload = extractPatientData(visitData);
      console.log("Payload:", payload);

      const endpointMap = {
        updateExistingCaseVisit: updateExistingCaseVisit,
        addNewCaseVisit: addNewCaseVisit,
        addExistingCaseVisit: addExistingCaseVisit,
      };

      if (endpointMap[updateType]) {
        await endpointMap[updateType](payload);
        console.log(`Successfully executed ${updateType}`);
      } else {
        console.error("Invalid update type:", updateType);
      }
    } catch (error) {
      console.error(
        `Error in ${updateType}:`,
        error.response ? error.response.data : error.message
      );
    }
  };

  const extractPatientData = (visitData) => ({
    patientInfo: visitData?.patientInfo?.[0] || {},
    patientSymptoms: visitData?.patientSymptoms?.[0] || {},
    patientHistory: visitData?.patientHistory?.[0] || {},
    patientClinicalInvestigation: visitData?.patientClinicalInvestigation?.[0] || {},
    patientAnyavyadhi: visitData?.patientAnyavyadhi?.[0] || {},
    patientKulawrutta: visitData?.patientKulawrutta?.[0] || {},
    patientTapasanya: visitData?.patientTapasanya?.[0] || {},
    patientArtawawrutta: visitData?.patientArtawawrutta?.[0] || {},
    patientDiagnosis: visitData?.patientDiagnosis?.[0] || {},
    patientTreatment: visitData?.patientTreatment?.[0] || {},
    patientPayment: visitData?.patientPayment?.[0] || {},
    patientNextVisit: visitData?.patientNextVisit?.[0] || {}
  });

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-10">
        <h3
          className="text-2xl md:text-4xl mb-3 tracking-tight"
          style={{
            fontFamily: theme.fonts?.serif,
            color: theme.text
          }}
        >
          Next Visit & Appointment
        </h3>
        <div className="h-1 w-12 rounded-full mb-6" style={{ backgroundColor: theme.primary }} />
        <p className="text-sm md:text-base opacity-60 font-medium" style={{ color: theme.textSecondary }}>
          Scheduling the next follow-up and managing the clinical calendar.
        </p>
      </div>

      {patientData && (
        <AppointmentCalendar
          mode="edit"
          existingAppointments={allAppointments}
          onSaveAppointment={onSubmitHandler}
          doctorInfo={doctorInfo}
          patientData={{
            PATIENT_ID: patientData.PATIENT_ID,
            CASE_ID: selectedCase || caseId,
            VISIT_ID: selectedVisit || visitId
          }}
          loading={loading}
          fetchAppointment={fetchAllAppointments}
          userRole={doctorInfo?.MANDALI_ROLE}
          clinicId={doctorInfo?.CLINIC_ID}
        />
      )}
    </div>
  );
};

export default EditPatientNextVisit;